import React, { useState } from 'react';

interface ShareStoryModalProps {
  projectName: string;
  author: string;
  pages: Array<{ id: string; [key: string]: any }>;
  onClose: () => void;
  onShare: (data: any) => void;
  onPublishToGallery: (data: any) => void;
}

const ShareStoryModal: React.FC<ShareStoryModalProps> = ({
  projectName = "My Comic", 
  author = "Young Artist", 
  pages = [],
  onClose, 
  onShare,
  onPublishToGallery 
}) => {
  const [shareType, setShareType] = useState('gallery');
  const [storyTitle, setStoryTitle] = useState(projectName);
  const [storyDescription, setStoryDescription] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('adventure');
  const [isPublic, setIsPublic] = useState(true);
  const [allowComments, setAllowComments] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  const theme = {
    primary: '#FF6B35',
    secondary: '#9B59B6',
    accent: '#F39C12',
    success: '#2ECC71',
    pink: '#E91E63',
    blue: '#3498DB'
  };

  const genres = [
    { id: 'adventure', name: 'Adventure', icon: '🗺️', color: theme.accent },
    { id: 'superhero', name: 'Superhero', icon: '🦸', color: theme.blue },
    { id: 'fantasy', name: 'Fantasy', icon: '🧙', color: theme.secondary },
    { id: 'sci-fi', name: 'Sci-Fi', icon: '🚀', color: theme.primary },
    { id: 'comedy', name: 'Comedy', icon: '😂', color: theme.pink },
    { id: 'mystery', name: 'Mystery', icon: '🔍', color: '#6C757D' },
    { id: 'friendship', name: 'Friendship', icon: '👫', color: theme.success },
    { id: 'family', name: 'Family', icon: '👨‍👩‍👧‍👦', color: '#FD7E14' }
  ];

  const shareOptions = [
    {
      id: 'gallery',
      name: 'Story Gallery',
      icon: '🌟',
      description: 'Share with the community and get featured!',
      color: theme.primary
    },
    {
      id: 'friends',
      name: 'Share with Friends',
      icon: '👨‍👩‍👧‍👦',
      description: 'Send a private link to family and friends',
      color: theme.blue
    },
    {
      id: 'classroom',
      name: 'Classroom Share',
      icon: '🏫',
      description: 'Share with your teacher and classmates',
      color: theme.success
    },
    {
      id: 'export',
      name: 'Export & Download',
      icon: '💾',
      description: 'Download your story to share anywhere',
      color: theme.secondary
    }
  ];

  const handleShare = () => {
    const shareData = {
      type: shareType,
      title: storyTitle,
      description: storyDescription,
      genre: selectedGenre,
      isPublic,
      allowComments,
      author,
      pages
    };

    if (shareType === 'gallery') {
      onPublishToGallery(shareData);
    } else {
      onShare(shareData);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        width: '95%',
        maxWidth: '800px',
        maxHeight: '90%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${theme.blue}, ${theme.pink})`,
          color: 'white',
          padding: '20px 30px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '1.8rem',
              fontWeight: '900'
            }}>
              🌟 Share Your Story
            </h2>
            <p style={{
              margin: '5px 0 0 0',
              opacity: 0.9,
              fontSize: '1rem'
            }}>
              Let the world see your amazing creation!
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 15px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '700'
            }}
          >
            ✕ Close
          </button>
        </div>

        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '30px'
        }}>
          {/* Share Type Selection */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '1.3rem',
              fontWeight: '700',
              color: '#333'
            }}>
              🎯 How would you like to share?
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '15px'
            }}>
              {shareOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => setShareType(option.id)}
                  style={{
                    padding: '20px',
                    borderRadius: '15px',
                    border: `3px solid ${shareType === option.id ? option.color : '#ddd'}`,
                    background: shareType === option.id 
                      ? `${option.color}10` 
                      : 'white',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div style={{
                    fontSize: '32px',
                    marginBottom: '10px'
                  }}>
                    {option.icon}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: '#333',
                    marginBottom: '6px'
                  }}>
                    {option.name}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#666',
                    lineHeight: '1.3'
                  }}>
                    {option.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Story Details Form */}
          {(shareType === 'gallery' || shareType === 'friends' || shareType === 'classroom') && (
            <div style={{
              background: '#f8f9fa',
              borderRadius: '15px',
              padding: '25px',
              marginBottom: '25px',
              border: '2px solid #e9ecef'
            }}>
              <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '1.2rem',
                fontWeight: '700',
                color: theme.primary
              }}>
                📝 Story Details
              </h3>

              {/* Title */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '8px'
                }}>
                  Story Title *
                </label>
                <input
                  type="text"
                  value={storyTitle}
                  onChange={(e) => setStoryTitle(e.target.value)}
                  placeholder="Give your story an awesome title!"
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    borderRadius: '10px',
                    border: '2px solid #ddd',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = theme.blue}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
              </div>

              {/* Description */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '8px'
                }}>
                  Description
                </label>
                <textarea
                  value={storyDescription}
                  onChange={(e) => setStoryDescription(e.target.value)}
                  placeholder="Tell readers what your story is about..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    borderRadius: '10px',
                    border: '2px solid #ddd',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    resize: 'vertical',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = theme.blue}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
              </div>

              {/* Genre Selection */}
              {shareType === 'gallery' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#333',
                    marginBottom: '12px'
                  }}>
                    Genre
                  </label>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: '10px'
                  }}>
                    {genres.map(genre => (
                      <button
                        key={genre.id}
                        onClick={() => setSelectedGenre(genre.id)}
                        style={{
                          padding: '12px',
                          borderRadius: '10px',
                          border: `2px solid ${selectedGenre === genre.id ? genre.color : '#ddd'}`,
                          background: selectedGenre === genre.id 
                            ? `${genre.color}10` 
                            : 'white',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <div style={{ fontSize: '20px', marginBottom: '4px' }}>
                          {genre.icon}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: '#333'
                        }}>
                          {genre.name}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Privacy & Settings */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '12px'
                }}>
                  Privacy & Settings
                </label>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    padding: '10px',
                    borderRadius: '8px',
                    background: isPublic ? `${theme.success}10` : 'white',
                    border: `2px solid ${isPublic ? theme.success : '#ddd'}`,
                    transition: 'all 0.3s ease'
                  }}>
                    <input
                      type="checkbox"
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e.target.checked)}
                      style={{ margin: 0 }}
                    />
                    <div>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#333'
                      }}>
                        🌍 Make story public
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#666'
                      }}>
                        Anyone can discover and read your story
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    padding: '10px',
                    borderRadius: '8px',
                    background: allowComments ? `${theme.blue}10` : 'white',
                    border: `2px solid ${allowComments ? theme.blue : '#ddd'}`,
                    transition: 'all 0.3s ease'
                  }}>
                    <input
                      type="checkbox"
                      checked={allowComments}
                      onChange={(e) => setAllowComments(e.target.checked)}
                      style={{ margin: 0 }}
                    />
                    <div>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#333'
                      }}>
                        💬 Allow comments
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#666'
                      }}>
                        Readers can leave friendly comments
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Story Preview */}
          <div style={{
            background: '#f8f9fa',
            borderRadius: '15px',
            padding: '20px',
            marginBottom: '25px',
            border: '2px solid #e9ecef'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.2rem',
                fontWeight: '700',
                color: theme.secondary
              }}>
                📖 Story Preview
              </h3>
              <button
                onClick={() => setShowPreview(!showPreview)}
                style={{
                  background: showPreview ? `${theme.secondary}20` : 'white',
                  color: showPreview ? theme.secondary : '#666',
                  border: `2px solid ${showPreview ? theme.secondary : '#ddd'}`,
                  borderRadius: '8px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                {showPreview ? 'Hide Preview' : 'Show Preview'}
              </button>
            </div>

            {showPreview && (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                border: '2px solid #e9ecef'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  marginBottom: '15px'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>
                    📚
                  </div>
                  <div>
                    <h4 style={{
                      margin: 0,
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      color: '#333'
                    }}>
                      {storyTitle || 'Untitled Story'}
                    </h4>
                    <div style={{
                      fontSize: '14px',
                      color: '#666',
                      marginTop: '2px'
                    }}>
                      by {author} • {pages.length} page{pages.length !== 1 ? 's' : ''}
                    </div>
                    {selectedGenre && shareType === 'gallery' && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: genres.find(g => g.id === selectedGenre)?.color + '20',
                        color: genres.find(g => g.id === selectedGenre)?.color,
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        marginTop: '6px'
                      }}>
                        <span>{genres.find(g => g.id === selectedGenre)?.icon}</span>
                        <span>{genres.find(g => g.id === selectedGenre)?.name}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {storyDescription && (
                  <div style={{
                    fontSize: '14px',
                    color: '#666',
                    lineHeight: '1.5',
                    marginBottom: '15px'
                  }}>
                    {storyDescription}
                  </div>
                )}

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                  gap: '8px'
                }}>
                  {pages.slice(0, 6).map((page, index) => (
                    <div
                      key={page.id}
                      style={{
                        aspectRatio: '1',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        border: '2px solid #e9ecef',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: '#666'
                      }}
                    >
                      📄
                    </div>
                  ))}
                  {pages.length > 6 && (
                    <div style={{
                      aspectRatio: '1',
                      background: `${theme.primary}10`,
                      borderRadius: '8px',
                      border: `2px dashed ${theme.primary}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: theme.primary
                    }}>
                      +{pages.length - 6}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Share Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '15px'
          }}>
            <button
              onClick={onClose}
              style={{
                padding: '12px 24px',
                borderRadius: '12px',
                border: '2px solid #ddd',
                background: 'white',
                color: '#666',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '700',
                transition: 'all 0.3s ease'
              }}
            >
              Cancel
            </button>

            <button
              onClick={handleShare}
              disabled={!storyTitle.trim()}
              style={{
                padding: '12px 24px',
                borderRadius: '12px',
                border: 'none',
                background: storyTitle.trim() 
                  ? `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`
                  : '#f0f0f0',
                color: storyTitle.trim() ? 'white' : '#ccc',
                cursor: storyTitle.trim() ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '700',
                transition: 'all 0.3s ease',
                flex: 1
              }}
            >
              {shareType === 'gallery' && '🌟 Publish to Gallery'}
              {shareType === 'friends' && '👫 Share with Friends'}
              {shareType === 'classroom' && '🏫 Share with Class'}
              {shareType === 'export' && '💾 Export Story'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareStoryModal;